import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { ButtonColor } from "@/components/Button/types";

import { CampaignStartFailure, CampaignToStart, campaignStateLabel } from "./outreachActivation";

/** `ask` — waiting for a choice; `busy` — assigning/starting; `failed` — reporting refused starts. */
export type OutreachActivationPhase = "ask" | "busy" | "failed";

/**
 * Above the Reach-out menu (z 1190/1200) and above the extension's lead panel (z 1500), which the
 * picker that opens this modal can be rendered inside.
 */
export const OUTREACH_ACTIVATION_Z_INDEX = 1600;

interface OutreachActivationModalProps {
	campaigns: CampaignToStart[];
	failures: CampaignStartFailure[];
	phase: OutreachActivationPhase;
	onStart: () => void;
	onAssignOnly: () => void;
	onCancel: () => void;
	onAcknowledge: () => void;
}

/**
 * Asks, when leads are about to be sent out into a campaign that is not sending, whether to start
 * that campaign too (PREC-3296) — and, when a start is refused, says so in place. It reports its
 * own outcome because the surfaces that open it have no toast that outlives them.
 */
export const OutreachActivationModal: FunctionComponent<OutreachActivationModalProps> = ({
	campaigns,
	failures,
	phase,
	onStart,
	onAssignOnly,
	onCancel,
	onAcknowledge,
}) => {
	const { t } = useTranslation();

	if (phase === "failed") {
		return (
			<Modal
				isOpened
				handleSave={onAcknowledge}
				size="sm"
				submitButtonText={t("outreachActivation.ok")}
				title={t("outreachActivation.failedTitle")}
				zIndex={OUTREACH_ACTIVATION_Z_INDEX}
			>
				<div className="flex flex-col gap-3 text-sm">
					<p className="text-text-meta">{t("outreachActivation.failedIntro")}</p>
					<ul className="flex flex-col gap-2">
						{failures.map((failure) => (
							<li
								key={failure.name}
								className="rounded-lg border border-status-error bg-status-error-light px-3 py-2"
							>
								<span className="font-semibold text-text">{failure.name}</span>
								<span className="block text-status-error">{failure.reason}</span>
							</li>
						))}
					</ul>
					<p className="text-text-meta">{t("outreachActivation.failedHint")}</p>
				</div>
			</Modal>
		);
	}

	const busy = phase === "busy";
	const count = campaigns.length;

	return (
		<Modal
			isOpened
			disableSecondSave={busy}
			handleClose={busy ? undefined : onCancel}
			handleSave={onStart}
			handleSecondSave={onAssignOnly}
			isLoading={busy}
			secondSubmitButtonText={t("outreachActivation.assignOnly")}
			size="sm"
			submitButtonColor={ButtonColor.SUCCESS}
			submitButtonText={t("outreachActivation.startAndAssign", { count })}
			title={t("outreachActivation.title", { count })}
			zIndex={OUTREACH_ACTIVATION_Z_INDEX}
		>
			<div className="flex flex-col gap-3 text-sm">
				<p className="text-text-meta">{t("outreachActivation.intro", { count })}</p>
				<ul className="flex flex-col gap-2">
					{campaigns.map((campaign) => (
						<li
							key={campaign.campaignId}
							className="flex flex-col gap-1 rounded-lg border border-border-light px-3 py-2"
						>
							<div className="flex items-center justify-between gap-2">
								<span className="truncate font-semibold text-text">{campaign.name}</span>
								<span className="shrink-0 whitespace-nowrap rounded-full bg-status-warning-light px-2 py-0.5 text-xs font-semibold text-status-warning">
									{t(campaignStateLabel(campaign.status).key)}
								</span>
							</div>
							{campaign.assignedLeads > 0 && (
								<span className="text-xs text-text-meta">
									{t("outreachActivation.alreadyAssigned", { count: campaign.assignedLeads })}
								</span>
							)}
						</li>
					))}
				</ul>
				<p className="text-xs text-text-meta">{t("outreachActivation.explainer")}</p>
			</div>
		</Modal>
	);
};
